import { cn } from '../../lib/utils'

interface SegmentedOption<T extends string> {
  value: T
  label: string
}

interface SegmentedProps<T extends string> {
  options: readonly SegmentedOption<T>[]
  value: T
  onValueChange: (value: T) => void
  'aria-label'?: string
  className?: string
}

/**
 * Pill-style exclusive choice. Plain buttons on purpose — no headless library
 * to swap out when this moves to another app.
 */
function Segmented<T extends string>({
  options,
  value,
  onValueChange,
  className,
  ...props
}: SegmentedProps<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={props['aria-label']}
      data-slot="segmented"
      className={cn('bg-bg-subtle inline-flex gap-0.5 rounded-md p-1', className)}
    >
      {options.map((option) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onValueChange(option.value)}
            className={cn(
              'rounded-sm px-4 py-1.5 text-sm font-medium transition-colors duration-100 ease-out outline-none',
              'focus-visible:outline-accent focus-visible:outline-2 focus-visible:outline-offset-1',
              active ? 'bg-bg-elev text-fg shadow-raise' : 'text-fg-muted hover:text-fg',
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

export { Segmented, type SegmentedOption, type SegmentedProps }
